// src/components/InventoryImages.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../scripts/api";
import { Upload, Trash2, X, ChevronLeft, ChevronRight, GripVertical } from "lucide-react";

const API_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  process.env.REACT_APP_API_BASE ||
  "";

function imgUrl(filename, thumb = false) {
  const name = thumb ? filename.replace(".webp", "_thumb.webp") : filename;
  return `${API_BASE}/api/uploads/inventory/${name}`;
}

export default function InventoryImages({ inventoryId, tableName = "all_inventory" }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null); // index
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/inventory-images/${inventoryId}`);
      setImages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load images", e);
    }
  }, [inventoryId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      for (const f of files) fd.append("images", f);
      fd.append("table_name", tableName);
      await api.post(`/inventory-images/${inventoryId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (imgId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/inventory-images/${imgId}`);
      setImages((prev) => prev.filter((i) => i.id !== imgId));
      if (lightbox !== null) setLightbox(null);
    } catch (e) {
      setError("Delete failed");
    }
  };

  // ── drag-to-reorder ──
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const onDragEnd = async () => {
    setDragIdx(null);
    // persist new order
    const items = images.map((img, i) => ({ id: img.id, sort_order: i }));
    try {
      await api.put("/inventory-images/reorder", { items });
    } catch (e) {
      console.error("Reorder failed", e);
    }
  };

  // ── lightbox nav ──
  const lbPrev = () => setLightbox((i) => (i > 0 ? i - 1 : images.length - 1));
  const lbNext = () => setLightbox((i) => (i < images.length - 1 ? i + 1 : 0));

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") lbPrev();
      if (e.key === "ArrowRight") lbNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>
          Images {images.length > 0 && <span style={{ fontWeight: 400, opacity: 0.6 }}>({images.length})</span>}
        </div>
        <label
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 14px", fontSize: 13, borderRadius: 8,
            border: "1px solid var(--btn-outline, #ccc)", cursor: "pointer",
            background: "var(--card-bg, #fff)", color: "var(--fg, #111)",
            opacity: uploading ? 0.5 : 1,
          }}
        >
          <Upload size={15} />
          {uploading ? "Uploading..." : "Upload"}
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 8 }}>{error}</div>}

      {images.length === 0 && !uploading && (
        <div className="muted" style={{ fontSize: 13 }}>No images yet. Click Upload to add photos.</div>
      )}

      {/* thumbnail grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
        {images.map((img, idx) => (
          <div
            key={img.id}
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDragEnd={onDragEnd}
            style={{
              position: "relative", borderRadius: 8, overflow: "hidden",
              border: dragIdx === idx ? "2px solid #3b82f6" : "1px solid var(--card-border, #ddd)",
              background: "#f5f5f5", aspectRatio: "1", cursor: "grab",
            }}
          >
            {/* drag handle indicator */}
            <div style={{
              position: "absolute", top: 4, left: 4, background: "rgba(0,0,0,0.45)",
              borderRadius: 4, padding: "2px 3px", zIndex: 2, display: "flex",
            }}>
              <GripVertical size={14} color="#fff" />
            </div>

            <img
              src={imgUrl(img.filename, true)}
              alt={img.original_name || "inventory"}
              onClick={() => setLightbox(idx)}
              style={{
                width: "100%", height: "100%", objectFit: "cover", cursor: "pointer",
                display: "block",
              }}
              loading="lazy"
            />

            {/* delete button */}
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(img.id); }}
              style={{
                position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)",
                border: "none", borderRadius: 4, padding: "3px 4px", cursor: "pointer",
                display: "flex", zIndex: 2,
              }}
              title="Delete image"
            >
              <Trash2 size={14} color="#fff" />
            </button>

            {/* caption / filename */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              background: "rgba(0,0,0,0.5)", color: "#fff",
              fontSize: 10, padding: "3px 6px", whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {img.caption || img.original_name || img.filename}
            </div>
          </div>
        ))}
      </div>

      {/* lightbox overlay */}
      {lightbox !== null && images[lightbox] && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.85)", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}
        >
          {/* close */}
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.15)",
              border: "none", borderRadius: 8, padding: 8, cursor: "pointer", zIndex: 2,
            }}
          >
            <X size={24} color="#fff" />
          </button>

          {/* prev */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); lbPrev(); }}
              style={{
                position: "absolute", left: 16, background: "rgba(255,255,255,0.15)",
                border: "none", borderRadius: 8, padding: 8, cursor: "pointer",
              }}
            >
              <ChevronLeft size={28} color="#fff" />
            </button>
          )}

          {/* image */}
          <img
            src={imgUrl(images[lightbox].filename, false)}
            alt={images[lightbox].original_name || ""}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, objectFit: "contain" }}
          />

          {/* next */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); lbNext(); }}
              style={{
                position: "absolute", right: 16, background: "rgba(255,255,255,0.15)",
                border: "none", borderRadius: 8, padding: 8, cursor: "pointer",
              }}
            >
              <ChevronRight size={28} color="#fff" />
            </button>
          )}

          {/* counter */}
          <div style={{
            position: "absolute", bottom: 20, color: "#fff", fontSize: 14,
            background: "rgba(0,0,0,0.5)", padding: "4px 14px", borderRadius: 20,
          }}>
            {lightbox + 1} / {images.length}
            {images[lightbox].caption && ` — ${images[lightbox].caption}`}
          </div>

          {/* delete in lightbox */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(images[lightbox].id); }}
            style={{
              position: "absolute", bottom: 20, right: 20,
              background: "rgba(220,38,38,0.8)", border: "none",
              borderRadius: 8, padding: "6px 12px", cursor: "pointer",
              color: "#fff", fontSize: 13, display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}
